import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getFullUrl } from '../utils/urlUtils';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const handleTokenExpiry = () => {
  localStorage.removeItem('authToken');
  window.location.href = getFullUrl('login');
}

export const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 60000,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (originalRequest.url === '/auth/login') {
      return Promise.reject(error);
    }
    if (error?.response?.status === 401 && error?.response?.data?.error === 'token_expired') {
      try {
        const refreshResponse = await axios.post(`${API_URL}/auth/refresh`, {}, { 
          withCredentials: true 
        });
        if (refreshResponse?.status === 200) {
          const newToken = refreshResponse.data.access_token;
          localStorage.setItem('authToken', newToken);

          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

          return axiosInstance(originalRequest);
        } else {
          handleTokenExpiry();
        }
      } catch (refreshError) {
        handleTokenExpiry();
        return Promise.reject(refreshError);
      }
    }

    if (error?.response?.status === 401) {
      handleTokenExpiry();
    }

    return Promise.reject(error);
  }
);

const get = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosInstance.get<T>(url, config);
};

const post = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosInstance.post<T>(url, data, config);
};

const patch = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosInstance.patch<T>(url, data, config);
};

const remove = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  return axiosInstance.delete<T>(url, config);
};

export default { get, post, patch, remove };
